import { NavLink, useLocation } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuth } from '@/hooks/use-auth';
import { useWorkspace } from '@/hooks/use-workspace';
import { isUnderRoute, type NavItem } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { Brand } from './brand';
import { RAIL_ITEM_CLASS } from './rail-styles';
import { UserMenu } from './user-menu';

/**
 * Item da sidebar: ícone com o nome do módulo como legenda (sem depender do mouse). Quando a
 * legenda é abreviada (`shortLabel`), o nome completo aparece no tooltip e no nome acessível.
 */
function RailLink({ item }: { item: NavItem }) {
  const { pathname } = useLocation();
  const active = isUnderRoute(pathname, item.to);
  const link = (
    <NavLink
      to={item.to}
      aria-current={active ? 'page' : undefined}
      aria-label={item.shortLabel ? item.label : undefined}
      className={cn(
        RAIL_ITEM_CLASS,
        active && 'bg-primary-soft text-primary hover:bg-primary-soft hover:text-primary',
      )}
    >
      <item.icon className="size-5 shrink-0" strokeWidth={1.75} aria-hidden />
      <span
        className={cn(
          'line-clamp-2 w-full text-center text-[10.5px] leading-tight font-medium tracking-tight',
          !active && 'text-muted-foreground',
        )}
      >
        {item.shortLabel ?? item.label}
      </span>
    </NavLink>
  );
  if (!item.shortLabel) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={10}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Sidebar compacta (desktop, 84 px): logo no topo, módulos (ícone + legenda) com rolagem e, na base,
 * configurações e perfil. As seções de cada módulo ficam nas abas do cabeçalho. Para
 * administradores, a lista segue a área de trabalho escolhida no cabeçalho.
 */
export function AppSidebar() {
  const { tenant } = useAuth();
  const { modules } = useWorkspace();
  const main = modules.filter((m) => m.placement !== 'footer');
  const footer = modules.filter((m) => m.placement === 'footer');

  return (
    <aside
      className="sticky top-0 hidden h-svh w-(--sidebar-width) shrink-0 flex-col items-center border-r border-sidebar-border bg-sidebar md:flex"
      aria-label="Barra lateral"
    >
      <NavLink
        to="/dashboard"
        aria-label="Início"
        className="flex h-(--header-height) w-full shrink-0 items-center justify-center rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/30"
      >
        <Brand logoUrl={tenant?.logoUrl} name={tenant?.tradeName} compact />
      </NavLink>
      <nav
        aria-label="Módulos"
        className="scrollbar-none flex min-h-0 w-full flex-1 flex-col items-center gap-0.5 overflow-y-auto py-2"
      >
        {main.map((item) => (
          <RailLink key={item.to} item={item} />
        ))}
      </nav>
      <div className="flex w-full shrink-0 flex-col items-center gap-0.5 border-t border-sidebar-border py-2">
        {footer.map((item) => (
          <RailLink key={item.to} item={item} />
        ))}
        <UserMenu variant="rail" />
      </div>
    </aside>
  );
}
